/**
 * @file Employee gear view (`#/me`) — read-only.
 *
 * Reads /employee_view (caller's row) and /device_view (devices where
 * currentHolderEmail == caller). NEVER reads /devices or /employees — those
 * carry financial fields employees shouldn't see; the rules deny anyway.
 *
 * No actions. If the data is wrong, copy says "ping IT" — there is no
 * in-app reporting flow per project_airlock.md.
 */

import { el, clear, formatDate } from '../utils.js';
import { Icons }                 from '../icons.js';
import { getCurrentUser, isAdmin } from '../auth.js';
import { viewAsPersonaEmail, isViewingAsAdmin } from '../view-as.js';
import { navigate }              from '../router.js';
import { findEmployeeViewByEmail, watchMyDeviceView } from '../db.js';
import { renderPageHead }        from '../page-header.js';
import { renderHeldDeviceRow }   from '../lib/device-row.js';
import { catRank }               from '../lib/category-order.js';
import { kvRows }                from '../lib/kv.js';

export async function renderMe(host) {
  clear(host);
  // View-as (test mode only): render the seeded persona's gear instead of
  // the signed-in admin's, so "User" / "Office Management" previews show a
  // realistic populated page. Null in production → the real user.
  const email = viewAsPersonaEmail() || getCurrentUser()?.email || '';

  host.appendChild(renderPageHead('Your gear', 'Everything STRV has on record as currently yours.'));

  // Find their employee view doc by email (in case the auth uid isn't
  // the same as the doc id — the sync keys by Bamboo bambooId-derived id).
  const profile = await findEmployeeViewByEmail(email);

  const profileCard = el('div', { class: 'card', style: 'margin-bottom:16px' });
  if (profile) {
    profileCard.appendChild(el('table', { class: 'kv' },
      ...kvRows([
        ['Name',       profile.fullName],
        ['Job title',  profile.jobTitle || '—'],
        ['Department', profile.department || '—'],
        ['Joined',     formatDate(profile.startDate)],
      ]),
    ));
  } else {
    profileCard.appendChild(el('p', { class: 'muted' },
      "We can't find a Bamboo record for ", email, ". If you're new, this should appear within 24 hours after the nightly sync; otherwise ping IT.",
    ));
  }
  host.appendChild(profileCard);

  const listHost = el('div', { class: 'stack' });
  host.appendChild(listHost);

  const skeleton = el('div', { class: 'skeleton', style: 'height:80px;margin-bottom:8px' });
  listHost.appendChild(skeleton);

  // Active Bamboo-tracked items the employee should see on /me. Phone
  // tariffs (Sim Card rows with no return date) are the obvious case;
  // any other "asset" Bamboo tracks that's not also in /device_view
  // would come through here too. Source of truth is Bamboo — the row
  // disappears the moment Finance fills in Date returned.
  const bambooItems = (profile?.assets || []).filter((a) => !a.dateReturned);
  const bambooRows = bambooItems
    .filter((a) => a.category === 'Sim Card') // only Sim Card for now; expand when other categories appear
    .map((a) => ({
      id:          `bamboo-asset-${a.bambooAssetId}`,
      category:    'phone_tariff',
      name:        a.description || 'Phone tariff',
      sn:          null,
      contextTags: [],
      source:      'bamboo',
    }));

  let renderedOnce = false;
  const unsub = watchMyDeviceView(email, (devices) => {
    clear(listHost);
    renderedOnce = true;
    const all = [...devices, ...bambooRows];
    if (!all.length) {
      listHost.appendChild(emptyState());
      return;
    }
    // Group by category for stable order: laptop, phone, then everything else.
    const sorted = all.sort((a, b) => {
      const ra = catRank(a.category);
      const rb = catRank(b.category);
      if (ra !== rb) return ra - rb;
      return (a.name || '').localeCompare(b.name || '');
    });
    // Admins viewing their OWN gear can click through to the device page.
    // Employees (and the view-as User/Office simulations) keep plain rows —
    // /devices/:id is an admin route. Bamboo pseudo-rows (phone tariffs)
    // have no /devices doc, so they stay non-clickable for everyone.
    const canOpenDevice = isAdmin() && isViewingAsAdmin();
    for (const d of sorted) {
      const clickable = canOpenDevice && d.source !== 'bamboo';
      listHost.appendChild(renderHeldDeviceRow(d, {
        admin: false,
        onClick: clickable ? () => navigate(`/devices/${d.id}`) : null,
      }));
    }
  });

  host.addEventListener('cleanup', unsub, { once: true });

  // Safety net — if the watcher never fires (offline?), surface a soft error
  // after a beat.
  setTimeout(() => {
    if (!renderedOnce) {
      clear(listHost);
      listHost.appendChild(el('div', { class: 'empty' },
        el('p', null, "Couldn't load your devices. Try refreshing."),
      ));
    }
  }, 8000);
}

function emptyState() {
  return el('div', { class: 'empty' },
    Icons.inbox(),
    el('p', null, 'No devices on record.'),
    el('p', { class: 'muted' }, 'If that looks wrong, ping IT.'),
  );
}
